/**
 * W-8EXP Form Hook
 * Custom hook for managing W-8EXP form state using model fields as single source of truth
 */

import { useState, useEffect, useCallback } from "react";
import { W8EXPFormModel } from "../Models/W8EXPModels";
import { W8EXPService } from "../services/W8EXPService";

export interface UseW8EXPFormOptions {
  initialData?: any;
}

export interface UseW8EXPFormReturn {
  model: W8EXPFormModel;
  isLoading: boolean;
  isSaving: boolean;
  errors: Record<string, string>;
  isValid: boolean;
  updateField: (field: string, value: string | boolean) => void;
  updateMultipleFields: (fields: Record<string, string | boolean>) => void;
  validate: () => boolean;
}

export const useW8EXPForm = (options: UseW8EXPFormOptions = {}): UseW8EXPFormReturn => {
  const { initialData } = options;

  // State management using model as single source of truth
  const [model, setModel] = useState<W8EXPFormModel>(() => {
    if (initialData) {
      return W8EXPService.fromApiResponse(initialData);
    }
    return new W8EXPFormModel();
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValid, setIsValid] = useState(false);

  // Update validation state when model changes
  useEffect(() => {
    const validationResult = model.validate();
    setErrors(validationResult.fieldErrors);
    setIsValid(validationResult.isValid);
  }, [model]);

  /**
   * Update a single field in the model
   */
  const updateField = useCallback((field: string, value: string | boolean) => {
    setModel((prevModel) => {
      const newModel = new W8EXPFormModel(prevModel);
      (newModel as any)[field] = value;
      return newModel;
    });
  }, []);

  /**
   * Update multiple fields in the model
   */
  const updateMultipleFields = useCallback((fields: Record<string, string | boolean>) => {
    setModel((prevModel) => {
      const newModel = new W8EXPFormModel(prevModel);
      Object.entries(fields).forEach(([field, value]) => {
        (newModel as any)[field] = value;
      });
      return newModel;
    });
  }, []);

  /**
   * Validate the current model
   */
  const validate = useCallback((): boolean => {
    const validationResult = model.validate();
    setErrors(validationResult.fieldErrors);
    setIsValid(validationResult.isValid);
    return validationResult.isValid;
  }, [model]);

  return {
    model,
    isLoading,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  };
};
